import { useState, useEffect } from "react";
import {
  useVoterData, useSearch, SearchBars, cardKeys,
  getPollVotes, savePollVote,
} from "../utils/voterUtils";

export default function VoterSelect() {
  const data = useVoterData();
  const {
    searchName, setSearchName,
    searchRname, setSearchRname,
    searchVid, setSearchVid,
    hasQuery, filtered,
  } = useSearch(data);

  const [selectedVids, setSelectedVids] = useState(new Set());
  const [toastMsg, setToastMsg] = useState("");

  useEffect(() => {
    const existing = getPollVotes().map((v) => v["VID"]);
    setSelectedVids(new Set(existing));
  }, []);

  const showToast = (msg, color = "#198754") => {
    setToastMsg({ msg, color });
    setTimeout(() => setToastMsg(""), 2500);
  };

  const handleSelect = (row) => {
    const vid = row["VID"];
    if (selectedVids.has(vid)) {
      showToast("⚠️ Already added to poll", "#f59f00");
      return;
    }
    savePollVote(row);
    setSelectedVids((prev) => new Set([...prev, vid]));
    showToast(`✅ Vote marked for ${row["VNAME"] || row["VNAMEENG"] || vid}`);
  };

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif" }}>
      {/* Banner */}
      <div style={{ lineHeight: 0 }}>
        <img
          src="/banner.jfif"
          alt="Banner"
          style={{ width: "100%", display: "block", maxHeight: "220px", objectFit: "cover" }}
        />
      </div>

      {/* Worker notice bar */}
      <div
        style={{
          background: "linear-gradient(135deg, #1864ab, #1971c2)",
          color: "white",
          padding: "10px 24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <span style={{ fontSize: "14px", fontWeight: "600" }}>
          🗳️ Vote Selection Mode — Mark voters who have voted
        </span>
        <span
          style={{
            background: "rgba(255,255,255,0.2)",
            padding: "4px 14px",
            borderRadius: "20px",
            fontSize: "13px",
            fontWeight: "bold",
          }}
        >
          {selectedVids.size} vote(s) marked
        </span>
      </div>

      <div className="container py-4">
        <h2 className="text-center mb-1 text-primary">🗳️ मतदाता चयन</h2>
        <p className="text-center text-muted small mb-4">
          Search a voter and click <strong>Mark as Voted</strong> to add them to the poll tracker
        </p>

        <SearchBars
          searchName={searchName} setSearchName={setSearchName}
          searchRname={searchRname} setSearchRname={setSearchRname}
          searchVid={searchVid} setSearchVid={setSearchVid}
        />

        <p className="text-muted small mb-4" style={{ fontSize: "12px" }}>
          💡 Results match from the <strong>start</strong> of each field.
        </p>

        {hasQuery && (
          <p className="text-secondary small mb-3">
            Showing <strong>{Math.min(filtered.length, 200)}</strong>
            {filtered.length > 200 ? ` of ${filtered.length}` : ""} result(s)
          </p>
        )}

        <div className="row">
          {filtered.slice(0, 200).map((row, i) => {
            const vid = row["VID"];
            const isSelected = selectedVids.has(vid);
            const keys = cardKeys(row);

            return (
              <div key={i} className="col-md-6 col-lg-4 mb-4">
                <div
                  className="card shadow h-100"
                  style={{
                    border: isSelected ? "2px solid #2f9e44" : "1px solid #dee2e6",
                    transition: "border-color 0.2s",
                  }}
                >
                  <div
                    className="card-header d-flex justify-content-between align-items-center"
                    style={{ background: isSelected ? "#d3f9d8" : undefined }}
                  >
                    <strong>Voter Card</strong>
                    <span className="badge bg-secondary">{row.VOTER_ID || row.VOTERID || vid}</span>
                  </div>

                  <div className="card-body">
                    <div className="d-flex">
                      <div
                        style={{
                          width: "90px", height: "110px",
                          border: "1px dashed gray",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: "12px", marginRight: "12px", flexShrink: 0,
                          backgroundColor: "#f8f9fa",
                        }}
                      >
                        Photo
                      </div>
                      <div className="row" style={{ flex: 1 }}>
                        {keys.map((key, idx) => {
                          let label = key;
                          const value = row[key] || "-";
                          if (key === "VNAME") label = "Name";
                          if (key === "RNAME") label = "Relative Name";
                          if (key === "VID")   label = "Voter Id";
                          return (
                            <div key={idx} className="col-6 mb-2">
                              <div style={{ fontSize: "13px" }}>
                                <strong>{label}:</strong>
                                <div style={{ marginTop: "2px" }}>{value}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="card-footer d-flex justify-content-between align-items-center">
                    <span className="text-muted small">Sabka Saath, Sabka Vikas</span>
                    <button
                      className={`btn btn-sm ${isSelected ? "btn-success" : "btn-outline-primary"}`}
                      onClick={() => handleSelect(row)}
                    >
                      {isSelected ? "✅ Voted" : "🗳️ Mark as Voted"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Toast */}
      {toastMsg && (
        <div
          style={{
            position: "fixed", bottom: "24px", right: "24px",
            background: toastMsg.color,
            color: "white", padding: "12px 20px",
            borderRadius: "8px", boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
            fontSize: "14px", zIndex: 9999,
            animation: "fadeIn 0.3s ease",
          }}
        >
          {toastMsg.msg}
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
      `}</style>
    </div>
  );
}
